/**
 * Shrinks a photo in the browser before upload: max 1280 px on the long side,
 * JPEG quality 0.85. Keeps requests small (the Worker caps images at ~5 MB)
 * and fast on mobile data.
 */
import { log } from './logger';

export interface PreparedImage { dataUrl: string; mimeType: 'image/jpeg'; bytesApprox: number }

export async function prepareImage(file: File, maxSide: number = 1280, quality: number = 0.85): Promise<PreparedImage> {
  if (!file.type.startsWith('image/')) throw new Error('That file is not an image.');
  const url: string = URL.createObjectURL(file);
  try {
    const img: HTMLImageElement = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('The image could not be read. HEIC photos may need converting to JPEG first.'));
      el.src = url;
    });
    const scale: number = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const w: number = Math.max(1, Math.round(img.naturalWidth * scale));
    const h: number = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (!ctx) throw new Error('Your browser could not process the image.');
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl: string = canvas.toDataURL('image/jpeg', quality);
    const bytesApprox: number = Math.round((dataUrl.length - dataUrl.indexOf(',') - 1) * 0.75);
    log.info('image.prepared', { original: file.size, width: w, height: h, bytesApprox });
    return { dataUrl, mimeType: 'image/jpeg', bytesApprox };
  } finally {
    URL.revokeObjectURL(url);
  }
}
